import type { OutboxItem } from './outbox-item.js';

/**
 * pending-warnings (pwa-offline-first Slice 7, task 7.8/scope item 3) — pure
 * domain predicates for the `/pending` page's own indicators: a "near
 * stuck" flag per syncing item, a "retrying" flag per pending item that has
 * already failed at least once, a pending-count/oldest-age summary (feeds
 * `storage-guard.ts`'s own `shouldWarnPendingCount`/`shouldWarnOldestAge`),
 * and which SYNCED items still carry a durable warning worth surfacing
 * beyond the transient success screen.
 */

/**
 * A syncing item past HALF its stuck-reset threshold is flagged as "near
 * stuck" — half, not the full threshold, so the operator gets advance
 * warning before `foreground-drainer.ts`'s own stuck-syncing reset would
 * fire (see `STUCK_SYNCING_RESET_THRESHOLD_MS`, passed in by the caller —
 * this module stays dependency-free from `offline/application`).
 */
const STUCK_WARNING_RATIO = 0.5;

export function isNearStuckThreshold(
  item: OutboxItem,
  now: Date,
  stuckThresholdMs: number,
): boolean {
  if (item.state !== 'syncing') return false;
  return (
    now.getTime() - new Date(item.updatedAt).getTime() > stuckThresholdMs * STUCK_WARNING_RATIO
  );
}

/** A pending item that has already failed and backed off at least once. */
export function isRetrying(item: OutboxItem): boolean {
  return item.state === 'pending' && item.attempts > 0;
}

/**
 * isActivelyPending — MEDIUM fix (adversarial review): true only for a
 * `pending`/`syncing` item, i.e. one genuinely "waiting to sync" on its
 * own. `rejected` (blocked on an explicit operator force/edit/discard),
 * `needs-auth` (blocked on a session recovery), and `held` (blocked, e.g.
 * cross-org) are NOT silently aging — each already has its own distinct
 * surface (RejectedItemCard, the needs-auth state label). Feeding them
 * into the pending-count/oldest-age banners, the `offline.pending` Sentry
 * tag, or the `offline.queue_stale` staleness check inflated those signals
 * and could keep `offline.queue_stale` alive even after every genuinely
 * stuck item was handled.
 */
export function isActivelyPending(item: OutboxItem): boolean {
  return item.state === 'pending' || item.state === 'syncing';
}

export interface PendingSummary {
  count: number;
  oldestCapturedAt: Date | null;
}

/** Feeds `storage-guard.ts`'s `shouldWarnPendingCount`/`shouldWarnOldestAge`. */
export function computePendingSummary(items: OutboxItem[]): PendingSummary {
  if (items.length === 0) return { count: 0, oldestCapturedAt: null };
  let oldestMs = Number.POSITIVE_INFINITY;
  for (const item of items) {
    const capturedMs = new Date(item.capturedAt).getTime();
    if (capturedMs < oldestMs) oldestMs = capturedMs;
  }
  return { count: items.length, oldestCapturedAt: new Date(oldestMs) };
}

/**
 * Design §9's Sentry `offline.queue_stale` threshold — DISTINCT from (and
 * shorter than) `storage-guard.ts`'s own 4h operator-facing UI warning:
 * this one drives an aggregate, team-facing Sentry signal, not something
 * shown to the operator directly.
 */
const QUEUE_STALE_THRESHOLD_MS = 60 * 60 * 1000; // 1h

/** True when the oldest pending item is older than the queue-stale threshold. */
export function isQueueStale(oldestCapturedAt: Date | null, now: Date): boolean {
  if (!oldestCapturedAt) return false;
  return now.getTime() - oldestCapturedAt.getTime() > QUEUE_STALE_THRESHOLD_MS;
}

/**
 * hasDurableWarning — true for a SYNCED item whose result carries something
 * the operator should still be able to review after the transient success
 * screen is gone: dropped incident photos, or a clamped (`offline_capped`)
 * checked-in time. `offline_verified` is NOT a warning — it means the
 * server accepted the claimed time as-is.
 */
export function hasDurableWarning(item: OutboxItem): boolean {
  if (item.state !== 'synced') return false;
  // MEDIUM fix (adversarial review) — an acknowledged item no longer needs
  // a look; see acknowledge-durable-warning.ts.
  if (item.acknowledgedAt) return false;
  return (
    item.result?.incidentPhotosDropped !== undefined ||
    item.result?.checkinTimeSource === 'offline_capped'
  );
}
